<template>
  <section :class="{'is-preview-event': isPreviewEvent, 'is-spectator-event': isSpectatorEvent}" class="slide-frame">
    <img v-if="bg.bgImage && !hasSlideImage" :src="cfResize(bg.bgImage, imageOpts)" :style="bg.bgImageStyle" class="slide-background">
    <img v-if="hasSlideImage" :src="cfResize(options.bgImage, imageOpts)" :style="{opacity: options.bgOpacity}" class="slide-background">
    <img v-if="bg.overlayImage" :src="cfResize(bg.overlayImage, imageOpts)" :style="bg.overlayStyle" class="slide-overlay">
    <slot />

    <div v-if="slide.isModerator && slide.isEvent && slide.slide && slide.slide.order === 1" class="direction next">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><defs><style>.a{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}</style></defs><title>interface-arrows-button-right</title><path class="a" d="M3.854.5,10,6.646a.5.5,0,0,1,0,.708L3.854,13.5" /></svg>
    </div>

    <div v-if="slide.isModerator && slide.isEvent && slide.slide && slide.slide.order !== 1" :class="{'hide-link': event.settings.hideJoinLink}" class="join-link flex-row">
      <p>
        <span v-if="!slide.displayTimer">join at </span>slideswith.com
      </p>
      <p class="code">/{{ slide.event.shortcode }}</p>
      <p @click.stop.prevent="toggleJoinLink()" class="show-hide">
        <span>&laquo;</span>
      </p>
    </div>

    <SlideTimer
      v-if="slide.displayTimer"
      @pause="slideshow.toggleTimer()"
      :secondsLeft="slideshow.timerPausedAt || $parent.secondsLeft"
      :totalSeconds="$parent.options.timerDuration"
      :paused="Number.isInteger(slideshow.timerPausedAt)"
    />

    <div v-if="showLogoOnDeck" :class="{'user-brand': hasUserLogo, 'logo-hidden': hideLogoThisSlide}" class="slide-branding">
      <template v-if="!hideLogoThisSlide">
        <img v-if="showUserLogo" :src="userLogoUrl">
        <div v-else-if="hasUserLogo && !isProUser && !isEvent" class="user-logo-image-wrapper">
          <img :src="hasUserLogo">
          <ProTagWithUpgradeTooltip @upgrade="startUpgrade" upgrade-intent="see your logo in live events" class="user-logo-pro-tag" />
        </div>
        <SlidesLogo v-else />
      </template>

      <!-- toggle show logo on specific slide -->
      <p v-if="isProUser && editing && !isEvent" @click="toggleHideLogo()" class="toggle-show-logo"><i class="fas fa-eye" /> {{ hideLogoThisSlide ? 'show logo' : 'hide logo' }}</p>
    </div>

    <div v-if="slide.isModerator && slide.isEvent" @mouseleave="closeSettings()" @click="closeSettings()" class="event-controls-wrap">
      <div @click.stop.prevent class="event-controls">
        <div @click="prevStep()" class="prev">
          <i class="fas fa-chevron-left" />
        </div>
        <div @click="toggleMute" class="volume">
          <i v-if="event.settings.isMuted" class="fas fa-volume-mute" />
          <i v-else class="fas fa-volume-up" />
        </div>
        <div @click="toggleSettings()" class="settings">
          <i class="fas fa-ellipsis-v" />
        </div>
        <div @click="nextStep()" class="next">
          <i class="fas fa-chevron-right" />
        </div>
      </div>
      <div v-if="!isPreviewEvent" @click.stop.prevent="startEndEvent()" class="end-event" data-cy="end-event-trigger">
        <!-- <i class="far fa-window-close" /> -->
        <i class="fas fa-times" />
      </div>
      <div @click.prevent.stop v-if="isEndingEvent" class="notification is-dark confirm-end">
        <p>Are you ready to end this event?</p>
        <div class="control-wrap">
          <div @click="resumeEvent()" class="button">Cancel</div>
          <div @click="endEvent($event)" class="button is-danger" data-cy="end-event-button">End Event</div>
        </div>
      </div>
      <div v-if="settingsOpen" @click.stop.prevent class="settings-menu">
        <div v-if="useSpectatorEvent" @click="toggleSpectatorEvent()" class="item fullscreen">
          <template v-if="isSpectatorEvent">
            <i class="fas fa-reply" /> Back to Main Event
          </template>
          <template v-else>
            <i class="far fa-eye" /> Show Spectator Event
          </template>
        </div>
        <div @click="toggleFullscreen()" class="item fullscreen">
          <template v-if="isFullscreen">
            <i class="fas fa-compress" /> Exit Fullscreen
          </template>
          <template v-else>
            <i class="fas fa-expand" /> Enter Fullscreen
          </template>
        </div>
        <b-tooltip :active="!canManagePlayers " label="Disabled when using anonymous player avatars" position="is-left">
          <div @click="openManagePlayers()" :class="{'is-disabled': !canManagePlayers}" class="item players">
            <i class="fas fa-users-cog" /> Manage Players
          </div>
        </b-tooltip>
      </div>
    </div>
  </section>
</template>

<script>
import { endEvent } from '~/queries/eventControls.gql'
import { saveEventSettings } from '~/queries/saveEventState.gql'
import SlidesLogo from '~/components/SlidesLogo'
import ManagePlayers from '~/components/modals/ManagePlayers'
import ProTagWithUpgradeTooltip from '~/components/upgrade/ProTagWithUpgradeTooltip'
import UpgradeAccount from '~/components/modals/UpgradeAccount'
import { avatarStyles } from '~/common/avatars'

export default {
  components: {
    ProTagWithUpgradeTooltip,
    SlideTimer: () => import('~/components/SlideTimer'),
    SlidesLogo
  },
  props: {
    theme: { default: () => { return {} } },
    options: { default: () => { return {} } },
    showLogo: { default: true },
    forceShowLogo: { default: false }
  },
  data () {
    return {
      imageOpts: {
        w: 1900,
        h: 1400,
        fit: 'scale-down'
      },
      endingEvent: false,
      hideEndEvent: false,
      settingsOpen: false
    }
  },
  computed: {
    bg () {
      return this.theme.background || {}
    },
    isFullscreen () {
      return this.$store.state.isFullscreen
    },
    hasSlideImage () {
      return this.options.bgImage && this.options.bgImage.length > 8
    },
    showLogoOnDeck () {
      if (!this.slide.isModerator) { return false }
      const showLogoForUser = !this.isProUser || this.metaData?.showModeratorBrand !== false
      return this.showLogo && this.slide.showLogo && showLogoForUser
    },
    hideLogoThisSlide () {
      return (this.canUseCustomLogo || !this.isProUser) && this.slide.options.hideLogoThisSlide === true && !this.forceShowLogo
    },
    hasUserLogo () {
      return this.metaData?.userLogoUrl || this.metaData?.userDarkLogoUrl
    },
    userLogoUrl () {
      if (this.isDark) {
        return this.metaData?.userDarkLogoUrl || this.metaData?.userLogoUrl
      } else {
        return this.metaData?.userLogoUrl || this.metaData?.userDarkLogoUrl
      }
    },
    canUseCustomLogo () {
      return this.isProUser || (!this.isEvent && !this.editing)
    },
    showUserLogo () {
      return this.hasUserLogo && this.canUseCustomLogo
    },
    overlay () {
      return this.theme.overlay || {}
    },
    slide () {
      return this.$parent
    },
    slideshow () {
      return this.$parent.slideshowComponent
    },
    event () {
      return this.$parent.event
    },
    isSpectatorEvent () {
      return this.event?.settings?.isSpectatorEvent
    },
    useSpectatorEvent () {
      return this.event?.settings?.useSpectatorEvent
    },
    slideHeight () {
      return this.$parent.slideHeight
    },
    slideWidth () {
      return this.$parent.slideWidth
    },
    isEvent () {
      return this.$parent.isEvent
    },
    editing () {
      return this.$parent.editing
    },
    isPreviewEvent () {
      return this.event?.settings?.isPreviewEvent || false
    },
    metaData () {
      if (this.$parent.metaData && Object.keys(this.$parent.metaData).length > 0) {
        return this.$parent.metaData
      }

      if (this.$parent.isEvent) {
        return this.$store.state.currentSlideDeck.metaData || {}
      }

      return {}
    },
    user () {
      return this.$store.state.user
    },
    isProUser () {
      return this.$store.getters.isProUser
    },
    isEndingEvent () {
      return !this.isPreviewEvent && !this.hideEndEvent && (this.endingEvent || this.slide.lastSlide)
    },
    brightness () {
      return this.options.colors ? this.options.colors.brightness : this.theme.colors.brightness
    },
    isDark () {
      return this.brightness === 'dark'
    },
    canManagePlayers () {
      return this.event && this.event.settings.avatarStyle !== avatarStyles.NONE
    }
  },
  methods: {
    prevStep () {
      this.$parent.slideshowComponent.previousStep()
    },
    nextStep () {
      this.$parent.slideshowComponent.nextStep()
    },
    toggleSettings () {
      this.settingsOpen = !this.settingsOpen
    },
    closeSettings () {
      this.settingsOpen = false
    },
    toggleHideLogo () {
      this.slide.toggleHideLogo()
    },
    toggleSpectatorEvent () {
      if (this.isSpectatorEvent) {
        this.$router.replace('/event/' + this.event.shortcode)
      } else {
        this.$router.replace('/spectate/' + this.event.shortcode)
      }
    },
    toggleFullscreen () {
      if (!document.fullscreenEnabled) {
        this.$buefy.toast.open({
          message: 'Fullscreen mode is not enabled in this browser',
          type: 'is-danger'
        })
        return
      }
      if (!document.fullscreenElement) {
        this.$store.commit('setFullscreen', true)
        document.documentElement.requestFullscreen()
      } else if (document.exitFullscreen) {
        this.$store.commit('setFullscreen', false)
        document.exitFullscreen()
      }
    },
    toggleMute () {
      if (this.event.settings.isMuted === true) {
        this.saveSetting('isMuted', false)
        this.slide.checkForMusicStart(this.slide.step)
      } else {
        this.saveSetting('isMuted', true)
        if (this.slide.sound) { this.slide.sound.pause() }
      }
    },
    openManagePlayers () {
      if (!this.canManagePlayers) { return }

      this.$buefy.modal.open({
        parent: this,
        component: ManagePlayers,
        trapFocus: true,
        width: 550,
        events: {
          'playerRemoved': this.$parent.slideshowComponent.removePlayer,
          'scoreUpdated': this.setNewPlayerScores
        },
        props: {
          players: this.event.participants,
          eventId: this.event.id
        }
      })
    },
    setNewPlayerScores () {
      if (!isNaN(this.slide.scorePosition)) {
        this.log('score update on leaderboard slide, refreshing scores')
        this.slide.setScoresByPlayer()
      }
    },
    toggleJoinLink () {
      if (this.event.settings.hideJoinLink === true) {
        this.saveSetting('hideJoinLink', false)
      } else {
        this.saveSetting('hideJoinLink', true)
      }
    },
    saveSetting (setting, value) {
      this.log('saving event settings: ')
      if (this.$parent.slideshowComponent.isRecap) {
        this.log('is recap, not updating event settings')
        return
      }
      const settings = Object.assign({}, this.event.settings)
      settings[setting] = value
      this.event.settings[setting] = value

      this.log('saving event settings: ', settings)

      this.$apollo.mutate({
        mutation: saveEventSettings,
        variables: {
          eventId: this.event.id,
          settings
        }
      })
    },
    startEndEvent () {
      // const eventId = this.event.id
      this.endingEvent = true
      this.hideEndEvent = false

      const elem = document.getElementById('active-event')
      elem.addEventListener('click', this.resumeEvent, { once: true })
    },
    resumeEvent () {
      this.endingEvent = false
      this.hideEndEvent = true
      const elem = document.getElementById('active-event')
      elem.removeEventListener('click', this.resumeEvent)
    },
    async endEvent ($event) {
      $event.target.classList.add('is-loading')

      const eventId = this.event.id
      const { data: { update_events_by_pk: event } } = await this.$apollo.mutate({
        mutation: endEvent,
        variables: {
          eventId
        }
      })

      const players = event.participants_aggregate.aggregate.count || 0
      const responses = event.responses_aggregate.aggregate.count || 0
      const settings = event.settings

      this.log('ending event. Settings:', settings)

      if (settings.spectatorEventId && settings.isMainEvent) {
        this.endSpectatorEvent(settings.spectatorEventId)
      }

      this.posthogCapture('event:end', {
        event_id: eventId,
        shortcode: event.shortcode,
        deck_id: event.slideDeck,
        num_players: players,
        num_responses: responses
      })

      this.log(`event ended with players: ${players}, responses: ${responses}`)

      await this.sendEventRecapEmails(eventId)

      $event.target.classList.remove('is-loading')

      this.$store.commit('setLastClosedEvent', { eventId, players, responses })
      this.$router.push('/decks')
    },
    async endSpectatorEvent (eventId) {
      this.log('ending spectator event')
      await this.$apollo.mutate({
        mutation: endEvent,
        variables: {
          eventId
        }
      })
    },
    /*
    ** sendEventRecapEmails Function is duplicated in EndEvent.vue
    */
    async sendEventRecapEmails (eventId) {
      this.log('sending recap emails to players for event ' + eventId)

      try {
        const resp = await this.$axios.post(process.env.SITE_URL + '/api/send-recap-emails', { eventId })
        this.log('recap email response:', resp)
      } catch (e) {
        console.error('Error sending event recap emails:', e)
      }
    },
    startUpgrade () {
      this.$buefy.modal.open({
        parent: this,
        fullScreen: true,
        component: UpgradeAccount,
        canCancel: false,
        customClass: 'upgrade-modal',
        trapFocus: true,
        props: {
          from_location: 'editor-logo-pro-tooltip',
          button_text: 'Upgrade your account to see your logo in live events'
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.direction {

  &.next{
    padding-right: .6em;
  }
  svg {
    width: 1.2em;
    height: 1.2em;
  }
}
.slide-background, .slide-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0; left: 0; bottom: 0; right: 0;
  object-fit: cover;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.slide-branding {
  position: absolute;
  margin: 0;
  padding: 0;
  bottom: .4em;
  left: .5em;
  width: 153px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;

  &.user-brand {
    width: 6em;
    max-width: 6em;
    height: 4em;

    width: min(6em, 220px);
    max-width: min(6em, 220px);
    height: min(4em, 150px);

    img {
      width: auto;
      max-width: 100%;
      max-height: 100%;
      opacity: 1;
    }
  }

  img {
    margin: 0;
    padding: 0;
    opacity: .6;
  }

  svg {
    width: 3.5em;
    min-width: 85px;
    max-width: 185px;
    opacity: .7;

    .slides, .with-friends {
      fill: var(--text);
    }
  }

  img, svg {
    transition: opacity .2s ease;
  }

  &.logo-hidden {
    img, svg {
      opacity: .1;
    }
  }

  .toggle-show-logo {
    position: absolute;
    bottom: .15em;
    left: 0;
    color: var(--primary);
    background-color: var(--fullContrast);
    padding: .1em .6em 0.15em;
    border-radius: 6px;
    opacity: 0;
    transition: opacity .2s ease;
    font-size: 18px;
    box-shadow: 0px 0px 5px var(--primary);
    cursor: pointer;

    i {
      margin-right: 2px;
      position: relative;
      top: 1px;
    }
  }

  &:hover {

    img, svg {
      opacity: 0;
    }

    .toggle-show-logo {
      opacity: .8;
      // text-shadow: 0px 0px 5px var(--primary);
    }
  }

  .user-logo-image-wrapper {
    position: relative;
  }
  .user-logo-pro-tag {
    position: absolute;
    top: -18px;
    right: 5px;
  }
}

.is-locked .slide-branding:hover,
.is-free .slide-branding:hover {
  svg, img {
    opacity: .7;
  }

  .user-logo-image-wrapper {
    svg, img {
      opacity: 1;
    }
  }

  .user-brand {
    img { opacity: 1; }
  }
  .toggle-show-logo {
    display: none;
  }
}

.is-event .slide-branding:hover {
  svg, img {
    opacity: 1;
  }
}

.join-link {
  position: absolute;
  top: .5em;
  left: 0;
  color: var(--text);
  font-size: .45em;
  background-color: var(--lowContrast);
  // border-bottom: .75em solid var(--lowContrast);
  // height: .45em;
  // background: linear-gradient(to right, transparent, var(--lowContrast));
  padding: 0em .3em 0em .6em;
  border-radius: 0em;
  border-bottom-right-radius: .2em;
  border-top-right-radius: .2em;
  text-align: left;
  transition: transform .2s ease;

  p {
    line-height: 1em;
  }

  .code {
    font-size: 1.65em;
    line-height: 1em;
    font-weight: 1000;
    text-transform: uppercase;
    margin-left: .1em;
  }

    &.hide-link {
      transform: translateX(-100%) translateX(1.5em);

      &:hover {
        transform: translateX(-100%) translateX(1.7em);
      }

      .show-hide {
        width: .8em;
        opacity: .6;
        top: .1em;

        span {
          transform: rotate(180deg);
        }
      }
    }

  .show-hide {
    width: 0em;
    font-size: 1.5em;
    text-align: center;
    position: relative;
    top: -.1em;
    margin-left: .1em;
    padding: .5em 0em .5em .1em;
    transform: scaleX(.8);
    opacity: 0;
    transition: width .3s ease, opacity .3s ease;
    cursor: pointer;

    span {
      display: inline-block;
    }

    &:hover {
      opacity: .9 !important;
    }
  }

  &:hover {
    .show-hide {
      width: .8em;
      opacity: .5;
    }
  }
}

.settings-menu {
  font-size: .5em;
  background-color: var(--fullContrast);
  padding: 0.5em;
  border-radius: 10px;
  position: absolute;
  bottom: 3.5em;
  right: 1em;
  z-index: 15;
  opacity: .65;
  transition: opacity .3s ease;

  &:hover {
    opacity: .75;
  }

  .item {
    padding: .3em .5em;
    border-radius: .2em;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: var(--fullContrast);
    filter: invert(1);

    &:hover {
      background-color: var(--lowContrast);
    }

    &.is-disabled {
      opacity: .5;
      cursor: not-allowed;
      pointer-events: none;
      &:hover {
        background-color: transparent;
      }
    }

    i {
      margin-right: .5em;
      width: 1em;
    }
  }
}

.event-controls-wrap {
  position: absolute;
  bottom: 0; right: 0;
  width: 10em; height: 3.5em;
  font-size: 1.1em;
  z-index: 20;
  // border: 2px solid rgba(255,255,255,.1);

  &:hover {
    .event-controls, .end-event {
      opacity: .6;
      transition: opacity .3s ease;
      transition-delay: 0;

      &:hover {
        opacity: 1;
        transition: opacity .2s ease;
        transition-delay: 0;
      }
    }
  }
}

.slide-1 .event-controls, .last-slide .event-controls {
  opacity: .8;
}

.slide-2.step-1 .event-controls {
  opacity: .6;
}

.slide-1 .end-event, .last-slide .end-event {
  opacity: .8;
}

.slide-2.step-1 .end-event {
  opacity: .6;
}

.end-event {
  opacity: 0;
  position: absolute;
  background-color: rgba(235, 7, 37, 0.658);
  width: 1em;
  height: 1em;
  border-radius: .2em;
  right: .5em;
  bottom: .5em;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  transition: opacity .5s ease;
  transition-delay: 2s;
  z-index: 10;
  cursor: pointer;

   i {
     font-size: .5em;
   }
}

.confirm-end {
  z-index: 10;
  width: 7em;
  height: 2.55em;
  padding: .45em .5em .4em .4em;
  position: absolute;
  bottom: 1.8em;
  right: .5em;
  text-align: right;
  font-family: BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif;

  & > div {
    padding: 0;
    margin: 0;

    .button {
      font-size: .35em;
    }
  }

  p {
    font-size: .4em;
    margin-bottom: .5em;
  }
}

.isPreviewEvent .event-controls, .is-preview-event .event-controls {
  right: .5em;
}

.event-controls {
  opacity: 0;
  position: absolute;
  bottom: .5em;
  right: 1.7em;
  background-color: var(--highContrast);
  border-radius: .2em;
  padding: .1em;
  display: flex;
  transition: opacity .5s ease;
  transition-delay: 2s;
  z-index: 10;

  div {
    font-size: .55em;
    padding: 0.25em .4em;
    border-radius: .4em;
    cursor: pointer;
    display: flex;
    color: var(--fullContrast);
    filter: invert(1);

    &:hover {
      background-color: var(--lowContrast);
    }
  }

  .prev + .next {
    margin-left: .5em;
  }
}
</style>
